import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSnackbar } from 'notistack';
import { useRetention } from '../RetentionContext';
import StageForm from '../components/StageForm';
import { SoftChip } from '../components/ui';
import { STAGE_FIELDS, missingRequired } from '../constants/stageFields';
import { RT } from '../theme/retentionTheme';
import {
  getRetentionProfile,
  saveInduction,
  saveRetentionProfile,
} from '../../../redux/features/retentionSlice';

// Stages 1–3 (Verify basics · First call · Induction + affordability). Values come from GET /retention/:id/profile,
// which the Onboarding tab loads once; saves go to PUT /profile (1, 2) or PUT /induction (3).
export default function ProfileStage({ stageNo }) {
  const { courseEnrollmentId, student, refresh } = useRetention();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { profile, saving } = useSelector((state) => state.retention);

  const data = profile.data?.profile;
  const values = useMemo(() => ({ ...(student || {}), ...(data || {}) }), [student, data]);
  const unavailable = !data || Boolean(profile.error);

  const save = async (changes) => {
    const thunk = STAGE_FIELDS[stageNo].save === 'induction' ? saveInduction : saveRetentionProfile;
    const res = await dispatch(thunk({ courseEnrollmentId, ...changes }));
    const ok = res.payload?.status === 'success';
    enqueueSnackbar(res.payload?.message || (ok ? 'Saved' : 'Could not save'), { variant: ok ? 'success' : 'error' });
    if (ok) {
      dispatch(getRetentionProfile(courseEnrollmentId));
      refresh();
    }
    return ok;
  };

  const verified = data?.basics_verified_at;
  // checked against saved values — the API refuses the sign-off on the same list (STAGE1_REQUIRED)
  const missing = stageNo === 1 ? missingRequired(1, values) : [];
  const extra =
    stageNo === 1 ? (
      verified ? (
        <SoftChip
          label={`Verified · ${data.basics_verified_by_name || ''} · ${dayjs(verified).format('DD MMM')}`}
          icon="solar:verified-check-bold"
          color={RT.success}
        />
      ) : (
        <Tooltip title={missing.length ? `Save these first: ${missing.join(', ')}` : ''}>
          <span>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={unavailable || saving || missing.length > 0}
              startIcon={<Icon icon="solar:verified-check-bold" width={16} />}
              onClick={() => save({ verify_basics: true })}
            >
              Mark verified
            </Button>
          </span>
        </Tooltip>
      )
    ) : null;

  return (
    <StageForm
      stageNo={stageNo}
      values={values}
      dynamicOptions={profile.data?.options}
      onSave={save}
      saving={saving}
      readOnly={unavailable}
      extraActions={extra}
    />
  );
}
